import { flowRight } from 'lodash';
import PropTypes from 'prop-types';
import React from 'react';
import classNames from 'classnames';
import { CSSTransitionGroup } from 'react-transition-group';
import Avatar from '../avatar';
import MoreButton from '../more-button';
import { PinIcon, LockIcon, TopCommentIcon } from '../../components/icons';
import PostActions from '../../containers/post-actions';
import TimeAgo from '../time-ago';
import withEnvironment from '../../hoc/with-environment';
import styles from './post-header.scss';
import withTranslate from '../../hoc/with-translate';
import withTopCommentColor from '../../hoc/with-top-comment-color';
import withExperiment from '../../hoc/with-experiment';
import { EXPERIMENT_TOP_COMMENT } from '../../constants/experiments';
import IconTooltip from '../icon-tooltip';

const TIME_ANIMATION_TIMEOUT = 1000;
const ICON_ANIMATION_TIMEOUT = 200;

const renderPinIcon = isPinned => {
  if (!isPinned) {
    return null;
  }

  return (
    <div className={styles.iconWrapper} key="pin">
      <div className={classNames(styles.icon, 'pin-icon')} data-hook="pin-icon">
        <PinIcon className="icon-secondary-fill" isPinned={isPinned}/>
      </div>
    </div>
  );
};

const renderLockIcon = isCommentsDisabled => {
  if (!isCommentsDisabled) {
    return null;
  }

  return (
    <div className={styles.iconWrapper} key="lock">
      <div className={classNames(styles.icon, 'lock-icon')} data-hook="lock-icon">
        <LockIcon className="icon-secondary-fill" isLocked={isCommentsDisabled}/>
      </div>
    </div>
  );
};

const renderTopCommentIcon = (topCommentColor, t) => {
  return (
    <div className={styles.iconWrapper} key="top-comment">
      <div className={styles.icon} data-hook="top-comment-icon">
        <IconTooltip text={t('comment.top-comment')}>
          <TopCommentIcon className="button-fill" color={topCommentColor}/>
        </IconTooltip>
      </div>
    </div>
  );
};

const avatarTypeMap = {
  single: Avatar.LARGE,
  search: Avatar.LARGE,
  large: Avatar.LARGE,
};

function PostHeader({ type, post, isAnimated, navigation: { ProfileLink }, showTopComment, topCommentColor, isTopCommentExperimentEnabled, t }) {
  const owner = post.owner || {};
  const className = classNames(styles.container, styles[type], 'post-header');
  const avatarType = avatarTypeMap[type];
  const moreButtonId = `more-button-${post._id}`;
  const shouldShowTopComment = isTopCommentExperimentEnabled && showTopComment && Boolean(post.topCommentId);
  return (
    <div className={className}>
      {owner.slug &&
      <object type="communities/link">
        <ProfileLink user={owner}>
          <Avatar type={avatarType} user={owner}/>
        </ProfileLink>
      </object>}
      <CSSTransitionGroup
        className={styles.date}
        transitionName={styles}
        transitionEnterTimeout={TIME_ANIMATION_TIMEOUT}
        transitionAppearTimeout={TIME_ANIMATION_TIMEOUT}
        transitionLeaveTimeout={0}
        transitionAppear={isAnimated}
      >
        <div className={styles.status} key="timeago">
          <TimeAgo time={post.createdDate}/>
        </div>
      </CSSTransitionGroup>
      <CSSTransitionGroup
        className={styles.icons}
        transitionName="post-header-icons"
        transitionEnterTimeout={ICON_ANIMATION_TIMEOUT}
        transitionLeaveTimeout={ICON_ANIMATION_TIMEOUT}
      >
        {renderPinIcon(post.isPinned)}
        {renderLockIcon(post.isCommentsDisabled)}
        {shouldShowTopComment && renderTopCommentIcon(topCommentColor, t)}
      </CSSTransitionGroup>
      <div className={styles.moreButton}>
        <MoreButton id={moreButtonId}>
          <PostActions post={post} focusOnCloseId={moreButtonId}/>
        </MoreButton>
      </div>
    </div>
  );
}

PostHeader.SINGLE = 'single';
PostHeader.SEARCH = 'search';
PostHeader.SMALL = 'small';
PostHeader.MEDIUM = 'medium';
PostHeader.LARGE = 'large';
PostHeader.MEMBERS_AREA = 'membersArea';

PostHeader.propTypes = {
  type: PropTypes.string,
  isAnimated: PropTypes.bool,
  post: PropTypes.object.isRequired,
  navigation: PropTypes.object,
  topCommentColor: PropTypes.string,
  showTopComment: PropTypes.bool,
  isTopCommentExperimentEnabled: PropTypes.bool,
  t: PropTypes.func,
};

export default flowRight(
  withEnvironment({ navigation: 'navigation' }),
  withTopCommentColor,
  withTranslate,
  withExperiment({ isTopCommentExperimentEnabled: EXPERIMENT_TOP_COMMENT }),
)(PostHeader);



// WEBPACK FOOTER //
// ./app/components/post-header/post-header.js